import { FC } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
	C_BTN,
	C_BTN_SECONDARY,
} from "../../components/ConfigListShell/buttonMetrics";

/**
 * SIMS-8622 — the pattern-change warning (design L580-596).
 *
 * AC1: on saving a changed pattern, state how many existing member IDs would no longer match, out
 * of the total, and offer continue or cancel. AC3: cancelling leaves the original pattern in place.
 *
 * COUNTS ONLY (NFR-4, Epic design AC 4). This component receives two numbers and has no prop that
 * could carry a member ID — the failing identifiers are never fetched, so there is nothing here to
 * leak. Do not add a "show which ones" affordance without a compliance decision.
 *
 * Metrics from the design: 500px (wider than the delete dialog's 480, which is itself wider than
 * the 440 discard prompt — each carries more), 12px radius, 18px/700 title, warning panel at 8px
 * radius with a 20px icon and 13px/18 text, actions 12px 24px 20px with a 12px gap.
 *
 * `Continue` is PRIMARY, not danger: the change is reversible and alters only future registrations.
 * The delete dialog's red is reserved for what cannot be undone.
 */

export interface PatternChangeWarningProps {
	open: boolean;
	/** Existing member IDs that would NOT match the candidate pattern. */
	mismatch: number;
	/** Total member IDs registered under this payer. */
	total: number;
	/**
	 * The check could not be completed (the request failed). The warning still opens, saying so —
	 * a failed check must not read as "nothing would break", which is exactly the false
	 * reassurance this story exists to prevent.
	 */
	unavailable?: boolean;
	onCancel: () => void;
	onContinue: () => void;
}

const PatternChangeWarning: FC<PatternChangeWarningProps> = ({
	open,
	mismatch,
	total,
	unavailable = false,
	onCancel,
	onContinue,
}) => (
	<Dialog
		open={open}
		onClose={onCancel}
		PaperProps={{
			sx: {
				width: "31.25rem",
				maxWidth: "100%",
				m: 2,
				borderRadius: "0.75rem",
			},
		}}
	>
		<DialogTitle
			sx={{
				padding: "1.25rem 1.5rem 0.5rem",
				fontSize: "1.125rem",
				fontWeight: 700,
				lineHeight: 1.5,
			}}
		>
			Change the member ID pattern?
		</DialogTitle>
		<DialogContent sx={{ padding: "0 1.5rem 1rem" }}>
			{/* The design's warning wash. `--warning-light` + `--warning-dark`, following
			    card-system.css rather than handoff §4's `--warning-ink`, which exists nowhere in
			    the frontend (OQ-13). */}
			<Box
				sx={{
					display: "flex",
					alignItems: "flex-start",
					gap: 1.25,
					backgroundColor: "warning.light",
					borderRadius: "0.5rem",
					padding: 1.5,
				}}
			>
				<WarningAmberIcon sx={{ fontSize: "1.25rem", color: "warning.dark" }} />
				<Typography
					sx={{
						fontSize: "0.8125rem",
						lineHeight: "1.125rem",
						color: "warning.dark",
					}}
				>
					{unavailable
						? "We could not check how many existing member IDs this pattern would affect. Continuing applies the new pattern to future registrations only — no existing member's ID is altered — but the count is unknown."
						: `${mismatch} of ${total} existing member IDs don't match the new pattern. They keep their IDs — this rule applies to new registrations only.`}
				</Typography>
			</Box>
		</DialogContent>
		<DialogActions sx={{ padding: "0.75rem 1.5rem 1.25rem", gap: 1.5 }}>
			{/* Cancel first, so a hurried Enter does not apply the change. */}
			<Button variant="outlined" onClick={onCancel} sx={C_BTN_SECONDARY}>
				Cancel
			</Button>
			<Button variant="contained" onClick={onContinue} sx={C_BTN}>
				Continue
			</Button>
		</DialogActions>
	</Dialog>
);

export default PatternChangeWarning;
